/* Drop countdown. The target comes from Theme settings → Next drop as three parts: the date as
 * typed (October 17, 2026 · 10/17/2026 · 2026-10-17), the time as typed (7:00 PM · 19:00) and the
 * IANA time zone they are written in (data-date, data-time, data-zone, see
 * snippets/countdown-target.liquid). The instant is worked out here with Intl, so summer and
 * winter time come out right and every visitor counts down to the same moment; a full ISO string
 * in data-target still works as a fallback. The clock and the "live" panel share one grid cell,
 * so switching between them never shifts the layout. */

const pad = (value) => String(value).padStart(2, '0');
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const WEEKDAY = /^(?:mon|tues|wednes|thurs|fri|satur|sun)day,?\s+/i;

// "October 17, 2026", "Oct 17 2026", "Friday, October 17th, 2026", "17 October 2026", "10/17/2026"
// (US order) or "2026-10-17" → [year, month, day]; anything else → null.
export function parseDate(text) {
  const value = String(text || '').trim().replace(WEEKDAY, '').replace(/(\d)(?:st|nd|rd|th)\b/gi, '$1');
  const month = (name) => (name.length < 3 ? 0 : MONTHS.findIndex((full) => full.startsWith(name.toLowerCase())) + 1);
  let found;
  let parts = null;
  if ((found = value.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/))) parts = [+found[1], +found[2], +found[3]];
  else if ((found = value.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/))) parts = [+found[3], +found[1], +found[2]];
  else if ((found = value.match(/^([a-z]+)\.?,?\s+(\d{1,2}),?\s+(\d{4})$/i))) parts = [+found[3], month(found[1]), +found[2]];
  else if ((found = value.match(/^(\d{1,2})\s+([a-z]+)\.?,?\s+(\d{4})$/i))) parts = [+found[3], month(found[2]), +found[1]];
  if (!parts) return null;
  const [year, mo, day] = parts;
  if (mo < 1 || mo > 12 || day < 1 || day > 31) return null;
  if (new Date(Date.UTC(year, mo - 1, day)).getUTCMonth() !== mo - 1) return null; // 31 February and friends
  return parts;
}

// "7:00 PM", "7pm", "7:30 p.m.", "19:00", "noon", "midnight" → [hours, minutes]; empty → noon; nonsense → null.
export function parseTime(text) {
  const value = String(text || '').trim().toLowerCase().replace(/\./g, '');
  if (!value || value === 'noon') return [12, 0];
  if (value === 'midnight') return [0, 0];
  const found = value.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
  if (!found) return null;
  let hours = +found[1];
  const minutes = +(found[2] || 0);
  if (minutes > 59) return null;
  if (found[3]) {
    if (hours < 1 || hours > 12) return null;
    if (found[3] === 'pm' && hours < 12) hours += 12;
    if (found[3] === 'am' && hours === 12) hours = 0;
  } else if (hours > 23) return null;
  return [hours, minutes];
}

// Minutes east of UTC that `zone` keeps at the instant `ts` (New York: -240 in summer, -300 in winter).
function zoneOffset(ts, zone) {
  const parts = {};
  new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
    .formatToParts(new Date(ts))
    .forEach((part) => { parts[part.type] = part.value; });
  const wall = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute, +parts.second);
  return (wall - Math.floor(ts / 1000) * 1000) / 60000;
}

// The instant (ms since the epoch) at which the wall clock in `zone` reads `date` `time`.
// NaN when the date or time can't be read, so the countdown shows "coming soon" instead of a wrong day.
export function resolveTarget({ date, time, zone, target } = {}) {
  const day = parseDate(date);
  if (!day) return target ? Date.parse(target) : NaN;
  const clock = parseTime(time);
  if (!clock) return NaN;
  const [year, month, dayOfMonth] = day;
  const [hours, minutes] = clock;
  const wall = Date.UTC(year, month - 1, dayOfMonth, hours, minutes);
  try {
    let ts = wall;
    for (let pass = 0; pass < 2; pass += 1) ts = wall - zoneOffset(ts, zone) * 60000; // a second pass settles a DST switch
    return ts;
  } catch {
    return new Date(year, month - 1, dayOfMonth, hours, minutes).getTime(); // unknown zone: the visitor's own
  }
}

function init(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = 'true';
  const target = resolveTarget(root.dataset);
  if (Number.isNaN(target)) {
    root.dataset.state = 'tba';
    return;
  }

  const date = root.querySelector('[data-countdown-date]');
  if (date) {
    date.textContent = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(target);
  }

  const units = Object.fromEntries(
    [...root.querySelectorAll('[data-unit]')].map((element) => [element.dataset.unit, element]),
  );

  // Write a number and, when it changed after the first paint, blink its unit softly (fua.css .is-tick).
  let primed = false;
  const show = (element, value) => {
    if (element.textContent === value) return;
    element.textContent = value;
    const unit = element.closest('.countdown__unit');
    if (!unit || !primed) return;
    unit.classList.remove('is-tick');
    void unit.offsetWidth; // restart the animation
    unit.classList.add('is-tick');
  };

  const tick = () => {
    const left = target - Date.now();
    if (left <= 0) {
      root.dataset.state = 'live';
      return;
    }
    root.dataset.state = 'counting';
    const seconds = Math.floor(left / 1000);
    show(units.days, pad(Math.floor(seconds / 86400)));
    show(units.hours, pad(Math.floor((seconds % 86400) / 3600)));
    show(units.minutes, pad(Math.floor((seconds % 3600) / 60)));
    show(units.seconds, pad(seconds % 60));
    primed = true;
    setTimeout(tick, 1000 - (Date.now() % 1000) + 10); // wake just after each whole second
  };
  tick();
}

document.querySelectorAll('[data-countdown]').forEach(init);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-countdown]').forEach(init);
});
